import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { executeQuery } from "../../../utils";
import Button from "../../../student/components/generic/button";
import { CourseStandard } from "DeltaMathAPI";
import {
  DMModal,
  DMModalContent,
  DMModalFooter,
  DMModalTitle,
} from "../../../shared/DMModal";
import { useState } from "react";
import { useDeltaToastContext } from "../../../shared/contexts/ToasterContext";

const STANDARDS_PREVIEW_QUERY_KEY = "allPreviewStandards";

export const BulkApproveStandardsBanner: React.FC<{
  onApproved: () => void;
}> = ({ onApproved }) => {
  const toastContext = useDeltaToastContext();
  const queryClient = useQueryClient();
  const [showModal, setShowModal] = useState(false);

  const { data: standardsData } = useQuery<
    { standards: CourseStandard[]; success: true } | { success: false }
  >({
    queryKey: [STANDARDS_PREVIEW_QUERY_KEY],
    queryFn: () => executeQuery({ path: "/standards/manage/all/preview" }),
    refetchOnWindowFocus: false,
  });

  const { mutate: approveStandards, isPending } = useMutation({
    mutationFn: () =>
      executeQuery({ path: "/standards/approve/bulk", method: "post" }),
    onSuccess: () => {
      toastContext.addToast({
        message: "Standards approved successfully",
        status: "Success",
      });
      queryClient.invalidateQueries({
        queryKey: [STANDARDS_PREVIEW_QUERY_KEY],
      });
      onApproved();
      setShowModal(false);
    },
    onError: (err) => {
      toastContext.addToast({
        message: "Unable to approve standards",
        status: "Error",
      });
    },
  });

  return standardsData?.success && standardsData.standards.length > 0 ? (
    <>
      <div className="mt-2 flex items-center gap-2 rounded border border-dm-warning-500 bg-dm-warning-100 px-4 py-2 text-sm">
        {standardsData.standards.length === 1 ? (
          <p>
            <strong>1</strong> standard needs approval.
          </p>
        ) : (
          <p>
            <strong>{standardsData.standards.length}</strong> standards need
            approval.
          </p>
        )}

        <Button
          className="!p-0 text-sm font-bold"
          type="link"
          onClick={() => setShowModal(true)}
        >
          Review and approve standards
        </Button>
      </div>

      <DMModal visible={showModal} onClose={() => setShowModal(false)}>
        <DMModalTitle>Review Standards</DMModalTitle>
        <DMModalContent>
          <p>The following standards have been updated and need approval:</p>
          <ul className="flex w-full flex-col gap-2">
            {standardsData.standards.map((standard) => (
              <li
                key={standard._id}
                className="flex gap-3 border-b border-dm-charcoal-100 pb-2 last:border-b-0 last:pb-0"
              >
                <span className="flex w-8 items-center justify-center rounded bg-dm-charcoal-100 text-sm font-bold text-dm-charcoal-500">
                  {standard.code}
                </span>
                {standard.name}
              </li>
            ))}
          </ul>
        </DMModalContent>
        <DMModalFooter>
          <Button type="outline" onClick={() => setShowModal(false)}>
            Cancel
          </Button>
          <Button isLoading={isPending} onClick={() => approveStandards()}>
            Approve Standards
          </Button>
        </DMModalFooter>
      </DMModal>
    </>
  ) : null;
};
